import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { BottomNavBar } from "@/components/BottomNavBarV2";
import ErrorBoundary from "@/components/ErrorBoundary";
import { NuriNotificationProvider } from "@/components/NuriNotification";
import { GroceryCartBadge } from "@/components/GroceryCartBadge";
import { PageTransition } from "@/components/PageTransition";
import { QuickActionSidebar } from "@/components/QuickActionSidebar";
import GlobalMicButton from "@/components/GlobalMicButton";

// Critical-path: eager load
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import LogMeal from "@/pages/LogMeal";

// Lazy-loaded pages
const MealDetail = React.lazy(() => import("@/pages/MealDetail"));
const MealHistory = React.lazy(() => import("@/pages/MealHistory"));
const WeeklySummary = React.lazy(() => import("@/pages/WeeklySummary"));
const MealPrepLibrary = React.lazy(() => import("@/pages/MealPrepLibrary"));
const MealPrepSunday = React.lazy(() => import("@/pages/MealPrepSunday"));
const SettingsPage = React.lazy(() => import("@/pages/Settings"));
const MealPlanCalendar = React.lazy(() => import("@/pages/MealPlanCalendar"));
const FoodDetailView = React.lazy(() => import("@/pages/FoodDetailView"));
const CoachDashboard = React.lazy(() => import("@/pages/CoachDashboard"));
const ClientDetailView = React.lazy(() => import("@/pages/ClientDetailView"));
const GroceryList = React.lazy(() => import("@/pages/GroceryList"));
const FridgeScanner = React.lazy(() => import("@/pages/FridgeScanner"));
const AICoach = React.lazy(() => import("@/pages/AICoach"));
const DailySummary = React.lazy(() => import("@/pages/DailySummary"));
const BatchUploadPage = React.lazy(() => import("@/pages/BatchUploadPage"));
const RecipeDetailPage = React.lazy(() => import("@/pages/RecipeDetailPage"));
const CustomRecipes = React.lazy(() => import("@/pages/CustomRecipes"));
const OptimizeDay = React.lazy(() => import("@/pages/OptimizeDay"));
const VoiceJournal = React.lazy(() => import("@/pages/VoiceJournal"));
const Challenges = React.lazy(() => import("@/pages/Challenges"));
const Recipes = React.lazy(() => import("@/pages/Recipes"));
const MyPlans = React.lazy(() => import("@/pages/MyPlans"));
const Progress = React.lazy(() => import("@/pages/Progress"));
const QuickLog = React.lazy(() => import("@/pages/QuickLog"));
const Goals = React.lazy(() => import("@/pages/Goals"));
const MealTemplates = React.lazy(() => import("@/pages/MealTemplates"));
const GoalReport = React.lazy(() => import("@/pages/GoalReport"));
const AdminPanel = React.lazy(() => import("@/pages/AdminPanel"));
const RestaurantsHub = React.lazy(() => import("@/pages/RestaurantsHub"));
const RestaurantSearch = React.lazy(() => import("@/pages/RestaurantSearch"));
const FastingPage = React.lazy(() => import("@/pages/FastingPage"));
const RecoveryPage = React.lazy(() => import("@/pages/RecoveryPage"));
const ProgressPhotosPage = React.lazy(() => import("@/pages/ProgressPhotosPage"));
const AccountabilityPage = React.lazy(() => import("@/pages/AccountabilityPage"));
const PregnancyHub = React.lazy(() => import("@/pages/PregnancyHub"));
const DiabetesHub = React.lazy(() => import("@/pages/DiabetesHub"));
const CeliacHub = React.lazy(() => import("@/pages/CeliacHub"));
const AthleteHub = React.lazy(() => import("@/pages/AthleteHub"));
const SeniorHub = React.lazy(() => import("@/pages/SeniorHub"));
const EdsMcasHub = React.lazy(() => import("@/pages/EdsMcasHub"));
const FamilyHub = React.lazy(() => import("@/pages/FamilyHub"));
const EdRecoveryHub = React.lazy(() => import("@/pages/EdRecoveryHub"));
const ModeComparePage = React.lazy(() => import("@/pages/ModeComparePage"));
const DiscoverPage = React.lazy(() => import("@/pages/DiscoverPage"));
const PlanPage = React.lazy(() => import("@/pages/PlanPage"));
const WhatToEatPage = React.lazy(() => import("@/pages/WhatToEatPage"));
const CookingModePage = React.lazy(() => import("@/pages/CookingModePage"));
const GLP1Page = React.lazy(() => import("@/pages/GLP1Page"));
const BeveragePage = React.lazy(() => import("@/pages/BeveragePage"));
const TermsOfService = React.lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const VoiceConversation = React.lazy(() => import("@/pages/VoiceConversation"));
const FastFoodPage = React.lazy(() => import("@/pages/FastFoodPage"));
const FamilyModePage = React.lazy(() => import("@/pages/FamilyModePage"));
const MotivationalPage = React.lazy(() => import("@/pages/MotivationalPage"));
const LabelComparePage = React.lazy(() => import("@/pages/LabelComparePage"));
const MyFoodsPage = React.lazy(() => import("@/pages/MyFoodsPage"));
const WeeklyPrepPlanner = React.lazy(() => import("@/pages/WeeklyPrepPlanner"));
const PlanTemplatesPage = React.lazy(() => import("@/pages/PlanTemplatesPage"));
const CreatorProfilePage = React.lazy(() => import("@/pages/CreatorProfilePage"));
const CommunityRecipesPage = React.lazy(() => import("@/pages/CommunityRecipesPage"));
const RecipeCollectionsPage = React.lazy(() => import("@/pages/RecipeCollectionsPage"));
const AdaptiveTargetsPage = React.lazy(() => import("@/pages/AdaptiveTargetsPage"));
const HydrationPage = React.lazy(() => import("@/pages/HydrationPage"));
const SymptomJournalPage = React.lazy(() => import("@/pages/SymptomJournalPage"));
const MedicationsPage = React.lazy(() => import("@/pages/MedicationsPage"));
const WeeklyReportPage = React.lazy(() => import("@/pages/WeeklyReportPage"));
const MyKitchenVersionsPage = React.lazy(() => import("@/pages/MyKitchenVersionsPage"));
const ShoppingModePage = React.lazy(() => import("@/pages/ShoppingModePage"));
const ComparePage = React.lazy(() => import("@/pages/ComparePage"));

function LazyFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: '#FDFBF7' }}>
      <div style={{ width: 28, height: 28, border: '3px solid #E8E3DB', borderTopColor: '#C67A5C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NavBarWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <BottomNavBar />;
}

function QuickActionWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <QuickActionSidebar />;
}

function GlobalMicWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <GlobalMicButton />;
}

function AppRouter() {
  const location = useLocation();

  // Keep-alive ping — prevents deployed backend from sleeping
  React.useEffect(() => {
    const ping = () => fetch('/api/ping').catch(() => {});
    ping(); // immediate ping on load
    const interval = setInterval(ping, 4 * 60 * 1000); // every 4 min
    return () => clearInterval(interval);
  }, []);
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <ErrorBoundary>
      <Suspense fallback={<LazyFallback />}>
      <PageTransition>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/voice" element={<ProtectedRoute><VoiceConversation /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/log-meal" element={<ProtectedRoute><LogMeal /></ProtectedRoute>} />
        <Route path="/meal/:mealId" element={<ProtectedRoute><MealDetail /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><MealHistory /></ProtectedRoute>} />
        <Route path="/weekly-summary" element={<ProtectedRoute><WeeklySummary /></ProtectedRoute>} />
        <Route path="/meal-prep" element={<ProtectedRoute><MealPrepLibrary /></ProtectedRoute>} />
        <Route path="/meal-prep-sunday" element={<ProtectedRoute><MealPrepSunday /></ProtectedRoute>} />
        <Route path="/meal-plan" element={<ProtectedRoute><MealPlanCalendar /></ProtectedRoute>} />
        <Route path="/food-detail" element={<ProtectedRoute><FoodDetailView /></ProtectedRoute>} />
        <Route path="/grocery-list" element={<ProtectedRoute><GroceryList /></ProtectedRoute>} />
        <Route path="/fridge-scanner" element={<ProtectedRoute><FridgeScanner /></ProtectedRoute>} />
        <Route path="/ai-coach" element={<ProtectedRoute><AICoach /></ProtectedRoute>} />
        <Route path="/daily-summary" element={<ProtectedRoute><DailySummary /></ProtectedRoute>} />
        <Route path="/batch-upload" element={<ProtectedRoute><BatchUploadPage /></ProtectedRoute>} />
        <Route path="/recipes" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />
        <Route path="/recipe/:recipeId" element={<ProtectedRoute><RecipeDetailPage /></ProtectedRoute>} />
        <Route path="/optimize" element={<ProtectedRoute><OptimizeDay /></ProtectedRoute>} />
        <Route path="/voice-journal" element={<ProtectedRoute><VoiceJournal /></ProtectedRoute>} />
        <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
        <Route path="/my-plans" element={<ProtectedRoute><MyPlans /></ProtectedRoute>} />
        <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
        <Route path="/quick-log" element={<ProtectedRoute><QuickLog /></ProtectedRoute>} />
        <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
        <Route path="/meal-templates" element={<ProtectedRoute><MealTemplates /></ProtectedRoute>} />
        <Route path="/goal-report" element={<ProtectedRoute><GoalReport /></ProtectedRoute>} />
        <Route path="/coach" element={<ProtectedRoute><AICoach /></ProtectedRoute>} />
        <Route path="/coach/pro" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
        <Route path="/coach/client/:clientId" element={<ProtectedRoute><ClientDetailView /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/restaurants" element={<ProtectedRoute><RestaurantsHub /></ProtectedRoute>} />
        <Route path="/restaurant-search" element={<ProtectedRoute><RestaurantsHub /></ProtectedRoute>} />
        <Route path="/fasting" element={<ProtectedRoute><FastingPage /></ProtectedRoute>} />
        <Route path="/recovery" element={<ProtectedRoute><RecoveryPage /></ProtectedRoute>} />
        <Route path="/progress-photos" element={<ProtectedRoute><ProgressPhotosPage /></ProtectedRoute>} />
        <Route path="/accountability" element={<ProtectedRoute><AccountabilityPage /></ProtectedRoute>} />
        <Route path="/pregnancy" element={<ProtectedRoute><PregnancyHub /></ProtectedRoute>} />
        <Route path="/diabetes" element={<ProtectedRoute><DiabetesHub /></ProtectedRoute>} />
        <Route path="/celiac" element={<ProtectedRoute><CeliacHub /></ProtectedRoute>} />
        <Route path="/athlete" element={<ProtectedRoute><AthleteHub /></ProtectedRoute>} />
        <Route path="/senior" element={<ProtectedRoute><SeniorHub /></ProtectedRoute>} />
        <Route path="/eds-mcas" element={<ProtectedRoute><EdsMcasHub /></ProtectedRoute>} />
        <Route path="/family" element={<ProtectedRoute><FamilyHub /></ProtectedRoute>} />
        <Route path="/ed-recovery" element={<ProtectedRoute><EdRecoveryHub /></ProtectedRoute>} />
        <Route path="/mode-compare" element={<ProtectedRoute><ModeComparePage /></ProtectedRoute>} />
        <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
        <Route path="/plan" element={<ProtectedRoute><PlanPage /></ProtectedRoute>} />
        <Route path="/custom-recipes" element={<ProtectedRoute><CustomRecipes /></ProtectedRoute>} />
        <Route path="/what-to-eat" element={<ProtectedRoute><WhatToEatPage /></ProtectedRoute>} />
        <Route path="/cooking-mode" element={<ProtectedRoute><CookingModePage /></ProtectedRoute>} />
        <Route path="/glp1" element={<ProtectedRoute><GLP1Page /></ProtectedRoute>} />
        <Route path="/beverages" element={<ProtectedRoute><BeveragePage /></ProtectedRoute>} />
        <Route path="/fast-food" element={<ProtectedRoute><FastFoodPage /></ProtectedRoute>} />
        <Route path="/family-mode" element={<ProtectedRoute><FamilyModePage /></ProtectedRoute>} />
        <Route path="/why-i-eat" element={<ProtectedRoute><MotivationalPage /></ProtectedRoute>} />
        <Route path="/label-compare" element={<ProtectedRoute><LabelComparePage /></ProtectedRoute>} />
        <Route path="/my-foods" element={<ProtectedRoute><MyFoodsPage /></ProtectedRoute>} />
        <Route path="/weekly-prep" element={<ProtectedRoute><WeeklyPrepPlanner /></ProtectedRoute>} />
        <Route path="/plan-templates" element={<ProtectedRoute><PlanTemplatesPage /></ProtectedRoute>} />
        <Route path="/creator/:creatorId" element={<ProtectedRoute><CreatorProfilePage /></ProtectedRoute>} />
        <Route path="/community-recipes" element={<ProtectedRoute><CommunityRecipesPage /></ProtectedRoute>} />
        <Route path="/cookbooks" element={<ProtectedRoute><RecipeCollectionsPage /></ProtectedRoute>} />
        <Route path="/kitchen-versions" element={<ProtectedRoute><MyKitchenVersionsPage /></ProtectedRoute>} />
        <Route path="/shopping-mode" element={<ProtectedRoute><ShoppingModePage /></ProtectedRoute>} />
        <Route path="/compare" element={<ProtectedRoute><ComparePage /></ProtectedRoute>} />
        <Route path="/adaptive-targets" element={<ProtectedRoute><AdaptiveTargetsPage /></ProtectedRoute>} />
        <Route path="/hydration" element={<ProtectedRoute><HydrationPage /></ProtectedRoute>} />
        <Route path="/symptom-journal" element={<ProtectedRoute><SymptomJournalPage /></ProtectedRoute>} />
        <Route path="/medications" element={<ProtectedRoute><MedicationsPage /></ProtectedRoute>} />
        <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReportPage /></ProtectedRoute>} />
      </Routes>
      </PageTransition>
      </Suspense>
      </ErrorBoundary>
      <NavBarWrapper />
      {/* Cart badge removed — now integrated into PageHeader */}
      <GlobalMicWrapper />
      <QuickActionWrapper />
    </>
  );
}

function OfflineIndicator() {
  const [offline, setOffline] = React.useState(!navigator.onLine);
  React.useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return (
    <div data-testid="offline-indicator" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, padding: '8px 16px',
      background: '#E5A84B', color: '#1A1A18', fontSize: 13, fontWeight: 600, textAlign: 'center',
      fontFamily: 'var(--font-sans)',
    }}>
      You're offline — changes will sync when you reconnect
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NuriNotificationProvider>
          <BrowserRouter>
            <OfflineIndicator />
            <AppRouter />
          </BrowserRouter>
          <Toaster />
        </NuriNotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
